
import type { PropType } from 'vue'
import type { AuditDetailField, AuditItem, ConfigurationsMessages } from '../../../models'
import type { AuditObjectTypeConfigProvider } from '../../../providers'
import { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'
import { AdapterBooleanUtils } from '@empathyco/playboard-core-domain'
import { deepEqual } from '@empathyco/x-utils'
import { defineComponent } from 'vue'
import { useAuditCopyToClipboard } from '../../../composables/use-audit-copy-to-clipboard'
import { CONTAINER } from '../../../config/inversify.config'
import { TYPES } from '../../../config/inversify.types'
import { ArrayUtils } from '../../../utils'
import AuditDetailsFieldValue from './audit-details-field-value.component.vue'

export interface GetLevelFieldsParams {
  slotId?: string
  previousState: any
  nextState: any
  level?: number
  parentId?: string
  parentKey?: string
  isLabelHidden?: boolean
}

export default defineComponent({
  name: 'AuditDetails',
  components: {
    AuditDetailsFieldValue,
  },
  props: {
    audit: {
      type: Object as PropType<AuditItem>,
      required: true,
    },
    arrayFieldConfigs: {
      type: Array as PropType<Array<{ field: string; id?: string }>>,
      default: () => [{ field: 'terms' }, { field: 'keywords' }],
    },
    ignoredFields: {
      type: Array as PropType<Array<string>>,
      default: () => ['meta', 'filters', 'pivots'],
    },
    messages: {
      type: Object,
      required: true,
    },
  },
  setup() {
    const { hasCopyToClipboard } = useAuditCopyToClipboard()

    return { hasCopyToClipboard }
  },
  computed: {
    fields(): AuditDetailField[] {
      const toolMessages: ConfigurationsMessages = this.$epb.messages.tools.configurations
      const conditionallyAddEnabledField = <T extends { enabled?: string }>(state: T): T =>
        state?.enabled === undefined
          ? state
          : {
              ...state,
              ...{
                enabled: AdapterBooleanUtils.toBoolean(state.enabled)
                  ? toolMessages.fields.enabled.states.enabled
                  : toolMessages.fields.enabled.states.disabled,
              },
            }

      return this.getLevelFields({
        previousState: conditionallyAddEnabledField(this.audit.payload.previousState),
        nextState: conditionallyAddEnabledField(this.audit.payload.nextState),
      })
    },
  },
  methods: {
    fieldKey(field: AuditDetailField): string {
      return field.key ?? (field.parentId ? `${field.parentId}-${field.id}` : field.id)
    },
    fieldKeyName(field: AuditDetailField): string {
      const hasDomainParent = Object.values(DomainEnum).includes(field.parentId as DomainEnum)
        ? (field.parentId as DomainEnum)
        : undefined
      const key = field.slotId || field.id
      return `${hasDomainParent ? `${hasDomainParent}-${key}` : key}.name`
    },
    getLevelFields(params: GetLevelFieldsParams): AuditDetailField[] {
      const {
        slotId,
        previousState = {},
        nextState = {},
        level = 0,
        parentId,
        parentKey,
        isLabelHidden,
      } = params

      const fieldIds = ArrayUtils.union(Object.keys(previousState), Object.keys(nextState))
      return fieldIds.reduce((fields: AuditDetailField[], fieldId: string) => {
        const previousFieldValue = previousState[fieldId]
        const nextFieldValue = nextState[fieldId]
        const isObject = this.isObject(previousFieldValue) || this.isObject(nextFieldValue)
        const arrayFieldConfig = this.getArrayFieldConfig(fieldId)
        const isParent = isObject || !!arrayFieldConfig
        const isFieldIgnored = this.ignoredFields.includes(fieldId)
        const currentKey = parentKey ? `${parentKey}-${fieldId}` : fieldId
        if (!isFieldIgnored) {
          fields.push({
            id: fieldId,
            key: currentKey,
            slotId,
            ...(!isParent && {
              previousValue: previousFieldValue,
              nextValue: nextFieldValue,
            }),
            level,
            parentId,
            isParent,
            isLabelHidden,
          })
        }
        if (isParent) {
          let childFields: AuditDetailField[]
          if (isObject) {
            childFields = this.getLevelFields({
              previousState: previousFieldValue,
              nextState: nextFieldValue,
              level: isFieldIgnored ? level : level + 1,
              parentId: fieldId,
              parentKey: currentKey,
            })
          } else {
            const fieldValueIds = ArrayUtils.union(
              this.getFieldValueIds(arrayFieldConfig, previousFieldValue),
              this.getFieldValueIds(arrayFieldConfig, nextFieldValue),
            )
            const isArrayOfStrings =
              this.isArrayOfStrings(previousFieldValue) || this.isArrayOfStrings(nextFieldValue)
            childFields = fieldValueIds.flatMap((id: string) => {
              return this.getLevelFields({
                slotId: slotId || fieldId,
                previousState: this.getStateComparison(id, arrayFieldConfig.id, previousFieldValue),
                nextState: this.getStateComparison(id, arrayFieldConfig.id, nextFieldValue),
                level: isFieldIgnored ? level : level + 1,
                parentId: fieldId,
                parentKey: currentKey,
                isLabelHidden: isArrayOfStrings,
              })
            })
          }
          fields = fields.concat(childFields)
        }
        return fields
      }, [])
    },
    getArrayFieldConfig(fieldId: string) {
      return this.arrayFieldConfigs.find(arrayFieldConfig => arrayFieldConfig.field === fieldId)
    },
    getFieldValueIds(arrayFieldConfig, fieldValues = []): string[] {
      if (arrayFieldConfig.id) {
        return fieldValues.map(fieldValue => fieldValue[arrayFieldConfig.id])
      }
      return fieldValues
    },
    getStateComparison(fieldValueId: string, arrayFieldConfigId: string, fieldValues = []) {
      let comparisonValue
      if (this.isArrayOfStrings(fieldValues)) {
        comparisonValue = fieldValues.includes(fieldValueId) ? fieldValueId : undefined
      } else {
        const fieldValue = fieldValues.find(value => value[arrayFieldConfigId] === fieldValueId)
        comparisonValue = fieldValue ? fieldValue.value : undefined
      }
      return { [fieldValueId]: comparisonValue }
    },
    getFieldName(field: AuditDetailField): string {
      if (field.isLabelHidden) {
        return
      }
      const fieldId = field.id.split('.').pop()
      const provider = CONTAINER.get<AuditObjectTypeConfigProvider>(
        TYPES.AuditObjectTypeConfigProvider,
      )
      const fieldNames = provider(this.audit.payload.objectType as string).fieldNames
      return fieldNames[fieldId]?.label ?? fieldId
    },
    isFieldModified(field: AuditDetailField): boolean {
      if (field.previousValue instanceof Date && field.nextValue instanceof Date) {
        return field.previousValue.getTime() !== field.nextValue.getTime()
      }
      return !deepEqual(field.previousValue, field.nextValue)
    },
    isObject(value): boolean {
      return (
        !!value && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)
      )
    },
    isArrayOfStrings(value: Array<unknown> = []): boolean {
      return value.some(element => typeof element === 'string')
    },
  },
})
